export interface ServiceAreaFact {
  label: { en: string; es: string };
  value: string;
}

export interface ServiceAreaRental {
  zone: string;
  oneBr: string;
  twoBr: string;
  threeBr: string;
}

export interface ServiceAreaFaq {
  q: { en: string; es: string };
  a: { en: string; es: string };
}

export interface ServiceAreaSectionTable {
  headers: string[];
  rows: string[][];
}

export interface ServiceAreaSectionLink {
  label: string;
  to: string;
}

export interface ServiceAreaSection {
  heading: string;
  paragraphs: string[];
  /** Optional data table rendered under the paragraphs. */
  table?: ServiceAreaSectionTable;
  /** Optional internal links rendered under the section. */
  links?: ServiceAreaSectionLink[];
}

export interface ServiceArea {
  slug: string;
  city: { en: string; es: string };
  state: string;
  metaTitle: { en: string; es: string };
  metaDescription: { en: string; es: string };
  intro: { en: string; es: string };
  neighborhoods: string[];
  facts: ServiceAreaFact[];
  rentalIncome: ServiceAreaRental[];
  hasPreApprovedPlans: boolean;
  preApprovedNote: { en: string; es: string };
  faq: ServiceAreaFaq[];
  content: { en: ServiceAreaSection[]; es: ServiceAreaSection[] };
  /** Optional geo coordinates for LocalBusiness JSON-LD. */
  geo?: { lat: number; lng: number };
  /** Optional zip codes served, emitted in JSON-LD areaServed. */
  postalCodes?: string[];
  /** Optional descriptive alt text for the hero image. */
  heroAlt?: { en: string; es: string };
  /** Optional hero image URL override (takes priority over city project photos). */
  heroImage?: string;

}


export const serviceAreas: ServiceArea[] = [
  // ─── SAN MARCOS ──────────────────────────────────────────────
  {
    slug: "san-marcos",
    city: { en: "San Marcos", es: "San Marcos" },
    state: "CA",
    metaTitle: {
      en: "San Marcos ADU Builders | Pre-Approved Plans, Lic #864642",
      es: "Constructores de ADU en San Marcos | Planos Pre-Aprobados",
    },
    metaDescription: {
      en: "San Marcos ADU builder with pre-approved plans ready to build — 50+ completed, 60-day move-in, price guarantee. Schedule your call (Lic #864642).",
      es: "Constructor de ADU en San Marcos. 50+ construidas, planos pre-aprobados, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta (#864642).",
    },
    intro: {
      en: "San Marcos is one of the most ADU-friendly cities in North County San Diego. With pre-approved ADU plans, streamlined permitting, and strong rental demand, it's an ideal location to build an accessory dwelling unit. EG Construction specializes in San Marcos ADU projects — from design to final inspection.",
      es: "San Marcos es una de las ciudades más amigables con ADUs en el norte del condado de San Diego. Con planos de ADU pre-aprobados, permisos simplificados y una fuerte demanda de alquiler, es una ubicación ideal para construir una unidad de vivienda accesoria. EG Construction se especializa en proyectos de ADU en San Marcos — desde el diseño hasta la inspección final.",
    },
    neighborhoods: ["San Elijo Hills", "Twin Oaks", "Richmar", "Lake San Marcos", "Knob Hill", "Discovery Hills"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,000 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "2–4 months" },
      { label: { en: "Mello-Roos", es: "Mello-Roos" }, value: "Some areas (check lot)" },
    ],
    rentalIncome: [
      { zone: "San Marcos (92069)", oneBr: "$1,800", twoBr: "$2,200", threeBr: "$2,500+" },
      { zone: "San Marcos (92078)", oneBr: "$1,850", twoBr: "$2,250", threeBr: "$2,600+" },
    ],
    hasPreApprovedPlans: true,
    preApprovedNote: {
      en: "San Marcos offers pre-approved ADU plans that can significantly reduce your permitting timeline. EG Construction works with these plans and can customize them to fit your property.",
      es: "San Marcos ofrece planos de ADU pre-aprobados que pueden reducir significativamente el tiempo de permisos. EG Construction trabaja con estos planos y puede personalizarlos para su propiedad.",
    },
    faq: [
      {
        q: { en: "How much does an ADU cost in San Marcos?", es: "¿Cuánto cuesta una ADU en San Marcos?" },
        a: {
          en: "A typical detached ADU in San Marcos costs between $150,000 and $300,000 depending on size, finishes, and site conditions. Mello-Roos fees may apply in some neighborhoods.",
          es: "Una ADU independiente típica en San Marcos cuesta entre $150,000 y $300,000 dependiendo del tamaño, acabados y condiciones del sitio. Las tarifas Mello-Roos pueden aplicar en algunos vecindarios.",
        },
      },
      {
        q: { en: "Does San Marcos allow two-story ADUs?", es: "¿San Marcos permite ADUs de dos pisos?" },
        a: {
          en: "San Marcos allows ADUs up to 16 feet in height. Two-story designs are possible but must comply with height and setback requirements.",
          es: "San Marcos permite ADUs de hasta 16 pies de altura. Los diseños de dos pisos son posibles pero deben cumplir con los requisitos de altura y retiros.",
        },
      },
      {
        q: { en: "What are pre-approved ADU plans in San Marcos?", es: "¿Qué son los planos pre-aprobados de ADU en San Marcos?" },
        a: {
          en: "Pre-approved plans are ADU designs already reviewed and approved by the city. Using them can cut months off your permitting timeline and reduce design costs.",
          es: "Los planos pre-aprobados son diseños de ADU ya revisados y aprobados por la ciudad. Usarlos puede reducir meses del tiempo de permisos y costos de diseño.",
        },
      },
      {
        q: { en: "Do I need a soils report for my San Marcos ADU?", es: "¿Necesito un informe de suelos para mi ADU en San Marcos?" },
        a: {
          en: "In most cases, yes. San Marcos requires a soils/geotechnical report, especially in hillside areas. EG Construction coordinates this for you.",
          es: "En la mayoría de los casos, sí. San Marcos requiere un informe de suelos/geotécnico, especialmente en áreas de ladera. EG Construction coordina esto por usted.",
        },
      },
      {
        q: { en: "Who are the best ADU builders in San Marcos?", es: "¿Quiénes son los mejores constructores de ADU en San Marcos?" },
        a: {
          en: "Compare San Marcos ADU builders on license status, finished local projects, whether design and permits are included, and price certainty. EG Construction is licensed since 2005 (#864642), built the Hillsboro ADU in San Marcos, and quotes one fixed price before permits.",
          es: "Compare constructores de ADU en San Marcos por licencia, proyectos locales terminados, si incluyen diseño y permisos, y certeza de precio. EG Construction tiene licencia desde 2005 (#864642), construyó la ADU Hillsboro en San Marcos y da un precio fijo antes de permisos.",
        },
      },
      {
        q: { en: "Can I build an ADU on a narrow lot in San Marcos?", es: "¿Puedo construir una ADU en un lote angosto en San Marcos?" },
        a: {
          en: "Often, yes. With 4-foot side and rear setbacks, a linear layout like our 749 sqft Hillsboro plan fits long, narrow San Marcos lots while keeping 2 bedrooms and 2 baths.",
          es: "Con frecuencia, sí. Con retiros laterales y traseros de 4 pies, un diseño lineal como nuestro plano Hillsboro de 749 pies² cabe en lotes largos y angostos de San Marcos con 2 recámaras y 2 baños.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in San Marcos?",
          paragraphs: [
            "San Marcos offers some of the best conditions for ADU construction in San Diego County. With pre-approved plans, reasonable permit timelines, and strong rental demand from nearby Cal State San Marcos and Palomar College, your ADU investment can generate $1,800–$2,500+ per month in rental income.",
            "The city's streamlined permitting process means less waiting and lower soft costs compared to other North County cities.",
          ],
        },
        {
          heading: "ADU Regulations in San Marcos",
          paragraphs: [
            "San Marcos allows ADUs up to 1,000 sqft with 4-foot side and rear setbacks. The maximum height is 16 feet. Under SB 13, no additional parking is required for ADUs. Some areas may be subject to Mello-Roos fees, so it's important to check your specific lot before starting.",
            "EG Construction handles all regulatory research and permit submissions for San Marcos ADU projects.",
          ],
        },
        {
          heading: "Our San Marcos ADU Process",
          paragraphs: [
            "We start with a discovery call to assess your property and goals. Our team handles design, engineering, permits, and full construction. Most San Marcos ADUs are completed in 60 business days from permit approval.",
            "As local specialists, we know the San Marcos planning department, common site challenges, and the best approaches for each neighborhood.",
          ],
        },
        {
          heading: "Choosing San Marcos ADU Builders",
          paragraphs: [
            "Look for San Marcos ADU builders who handle design, permits and construction under one contract, know the city's pre-approved plans, and can show finished work nearby. EG Construction built the Hillsboro ADU in San Marcos — a 749 sqft, 2-bed, 2-bath linear layout designed for a long, narrow lot.",
            "We're licensed since 2005 (CSLB #864642), have 50+ ADUs built across San Diego County, and give you one fixed price before permits are submitted.",
          ],
          links: [
            { label: "See the Hillsboro 749 sqft ADU floor plan", to: "/floor-plans/adu-floor-plan-749sqft-hillsboro-san-marcos" },
            { label: "Detached ADU builders in San Diego", to: "/detached-adu-san-diego" },
            { label: "ADU cost in San Diego 2026", to: "/blog/adu-cost-san-diego-2026" },
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en San Marcos?",
          paragraphs: [
            "San Marcos ofrece algunas de las mejores condiciones para la construcción de ADU en el condado de San Diego. Con planos pre-aprobados, tiempos de permiso razonables y una fuerte demanda de alquiler de la cercana Cal State San Marcos y Palomar College, su inversión en ADU puede generar $1,800–$2,500+ por mes en ingresos por alquiler.",
            "El proceso de permisos simplificado de la ciudad significa menos espera y costos blandos más bajos en comparación con otras ciudades del norte del condado.",
          ],
        },
        {
          heading: "Regulaciones de ADU en San Marcos",
          paragraphs: [
            "San Marcos permite ADUs de hasta 1,000 pies² con retiros laterales y traseros de 4 pies. La altura máxima es de 16 pies. Bajo SB 13, no se requiere estacionamiento adicional para ADUs. Algunas áreas pueden estar sujetas a tarifas Mello-Roos, por lo que es importante verificar su lote específico antes de comenzar.",
            "EG Construction maneja toda la investigación regulatoria y presentación de permisos para proyectos de ADU en San Marcos.",
          ],
        },
        {
          heading: "Nuestro Proceso de ADU en San Marcos",
          paragraphs: [
            "Comenzamos con una llamada de descubrimiento para evaluar su propiedad y metas. Nuestro equipo maneja diseño, ingeniería, permisos y construcción completa. La mayoría de las ADUs en San Marcos se completan en 60 días hábiles desde la aprobación del permiso.",
            "Como especialistas locales, conocemos el departamento de planificación de San Marcos, los desafíos comunes del sitio y los mejores enfoques para cada vecindario.",
          ],
        },
        {
          heading: "Cómo Elegir Constructores de ADU en San Marcos",
          paragraphs: [
            "Busque constructores de ADU en San Marcos que manejen diseño, permisos y obra en un solo contrato, conozcan los planos pre-aprobados de la ciudad y tengan proyectos terminados cerca. EG Construction construyó la ADU Hillsboro en San Marcos: 749 pies², 2 recámaras y 2 baños en un diseño lineal para un lote largo y angosto.",
            "Tenemos licencia desde 2005 (CSLB #864642), más de 50 ADUs construidas en el Condado de San Diego y un precio fijo antes de someter permisos.",
          ],
          links: [
            { label: "Ver el plano de la ADU Hillsboro de 749 pies²", to: "/floor-plans/adu-floor-plan-749sqft-hillsboro-san-marcos" },
            { label: "Constructores de ADU independientes en San Diego", to: "/detached-adu-san-diego" },
            { label: "Costo de una ADU en San Diego 2026", to: "/blog/adu-cost-san-diego-2026" },
          ],
        },
      ],
    },
  },

  // ─── CHULA VISTA ─────────────────────────────────────────────
  {
    slug: "chula-vista",
    city: { en: "Chula Vista", es: "Chula Vista" },
    state: "CA",
    metaTitle: {
      en: "ADU Construction in Chula Vista | Local Builder, Lic #864642",
      es: "Construcción de ADU en Chula Vista | Constructor Local",
    },
    metaDescription: {
      en: "ADU builder based in Chula Vista. Detached ADUs and garage conversions in Otay Ranch, Eastlake and 91910–91914. 50+ built, 4.8★, price guarantee.",
      es: "Constructor de ADU con oficina en Chula Vista. ADUs independientes y conversiones de garaje en Otay Ranch, Eastlake y 91910–91914. 50+ construidas, 4.8★.",
    },
    intro: {
      en: "Chula Vista is the second-largest city in the San Diego metro area and one of the most popular for ADU construction. With generous size allowances up to 1,200 sqft, the possibility of two-story ADUs, and strong rental demand across neighborhoods like Otay Ranch and Eastlake, building an ADU in Chula Vista is a smart investment.",
      es: "Chula Vista es la segunda ciudad más grande del área metropolitana de San Diego y una de las más populares para la construcción de ADU. Con asignaciones de tamaño generosas de hasta 1,200 pies², la posibilidad de ADUs de dos pisos y una fuerte demanda de alquiler en vecindarios como Otay Ranch y Eastlake, construir una ADU en Chula Vista es una inversión inteligente.",
    },
    neighborhoods: ["Otay Ranch", "Eastlake", "Rancho Del Rey", "Sunbow", "Rolling Hills Ranch", "Terra Nova"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft (two-story possible)" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–5 months" },
      { label: { en: "Pre-Approved Plans", es: "Planos Pre-Aprobados" }, value: "No (EG has ready-to-go designs)" },
    ],
    rentalIncome: [
      { zone: "91910 (West CV)", oneBr: "$1,900", twoBr: "$2,400", threeBr: "$2,900" },
      { zone: "91911 (South CV)", oneBr: "$1,850", twoBr: "$2,350", threeBr: "$2,850" },
      { zone: "91913 (Otay Ranch)", oneBr: "$2,100", twoBr: "$2,700", threeBr: "$3,200" },
      { zone: "91914 (East CV)", oneBr: "$2,000", twoBr: "$2,600", threeBr: "$3,100" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "While Chula Vista does not currently offer pre-approved ADU plans, EG Construction has ready-to-go designs that we've successfully permitted multiple times in the city. This speeds up your project significantly.",
      es: "Aunque Chula Vista no ofrece actualmente planos de ADU pre-aprobados, EG Construction tiene diseños listos que hemos permitido exitosamente múltiples veces en la ciudad. Esto acelera significativamente su proyecto.",
    },
    faq: [
      {
        q: { en: "Can I build a two-story ADU in Chula Vista?", es: "¿Puedo construir una ADU de dos pisos en Chula Vista?" },
        a: {
          en: "Yes, Chula Vista allows two-story ADUs within the 16-foot height limit. This is a great option for maximizing living space on smaller lots.",
          es: "Sí, Chula Vista permite ADUs de dos pisos dentro del límite de 16 pies de altura. Esta es una gran opción para maximizar el espacio habitable en lotes más pequeños.",
        },
      },
      {
        q: { en: "How much rental income can I earn from an ADU in Chula Vista?", es: "¿Cuánto ingreso por alquiler puedo obtener de una ADU en Chula Vista?" },
        a: {
          en: "Rental income varies by zip code. In Otay Ranch (91913), a 2-bedroom ADU can rent for $2,700/mo or more. Western Chula Vista averages around $2,400/mo for a 2-bedroom.",
          es: "Los ingresos por alquiler varían por código postal. En Otay Ranch (91913), una ADU de 2 habitaciones puede alquilarse por $2,700/mes o más. El oeste de Chula Vista promedia alrededor de $2,400/mes para una de 2 habitaciones.",
        },
      },
      {
        q: { en: "What are Chula Vista ADU setback requirements?", es: "¿Cuáles son los requisitos de retiros de ADU en Chula Vista?" },
        a: {
          en: "Chula Vista requires 4-foot setbacks from side and rear property lines for ADUs. Front setbacks follow the underlying zoning requirements.",
          es: "Chula Vista requiere retiros de 4 pies desde las líneas laterales y traseras de la propiedad para ADUs. Los retiros frontales siguen los requisitos de zonificación subyacentes.",
        },
      },
      {
        q: { en: "How long does the ADU permit process take in Chula Vista?", es: "¿Cuánto tarda el proceso de permisos de ADU en Chula Vista?" },
        a: {
          en: "The permit process in Chula Vista typically takes 3–5 months. EG Construction manages the entire process, including plan submissions, corrections, and inspections.",
          es: "El proceso de permisos en Chula Vista típicamente toma 3–5 meses. EG Construction maneja todo el proceso, incluyendo envío de planos, correcciones e inspecciones.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in Chula Vista?",
          paragraphs: [
            "Chula Vista's proximity to the border, military bases, and downtown San Diego creates consistent demand for rental housing. ADUs in neighborhoods like Otay Ranch and Eastlake command premium rents due to their excellent schools and amenities.",
            "With 1,200 sqft maximum size and two-story capability, you can build a spacious, attractive unit that generates significant monthly income.",
          ],
        },
        {
          heading: "Chula Vista ADU Regulations",
          paragraphs: [
            "Chula Vista allows detached ADUs up to 1,200 sqft with 4-foot setbacks. Two-story designs are permitted within the 16-foot height limit. No additional parking is required under SB 13.",
            "The city also allows Junior ADUs (JADUs) up to 500 sqft within the existing home footprint, which can be built in addition to a full ADU.",
          ],
        },
        {
          heading: "EG Construction in Chula Vista",
          paragraphs: [
            "Our main office is located in Chula Vista, making us the most accessible ADU builder for local homeowners. We've completed dozens of ADUs across the city and know the planning department inside and out.",
            "From Otay Ranch to Rolling Hills, we understand the unique site conditions and design preferences of each Chula Vista neighborhood.",
          ],
        },
        {
          heading: "Recent ADU Projects in Chula Vista",
          paragraphs: [
            "Our Chula Vista work includes a 749 sqft detached ADU built for rental income, a 574 sqft garage conversion rented out after completion, and a 337 sqft garage conversion built as a family studio. A 529 sqft detached ADU on the Toba plan is currently in design.",
            "Each of these projects started with a site visit, a feasibility check against Chula Vista's ADU rules, and a fixed price before permits. You can see photos, floor plans and timelines for each one in our projects section.",
          ],
        },
        {
          heading: "Detached ADU or Garage Conversion in Chula Vista?",
          paragraphs: [
            "Many Chula Vista lots in Otay Ranch, Eastlake and Rancho Del Rey have room for a detached ADU in the backyard. Older homes in western Chula Vista (91910 and 91911) often have a detached or attached garage that can become a full ADU with less new foundation work.",
            "We help you compare both options for your lot: size, cost, rental potential and how each fits your family's plans. If you already have approved plans, we can also build from them.",
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en Chula Vista?",
          paragraphs: [
            "La proximidad de Chula Vista a la frontera, bases militares y el centro de San Diego crea una demanda constante de vivienda de alquiler. Las ADUs en vecindarios como Otay Ranch y Eastlake obtienen alquileres premium debido a sus excelentes escuelas y amenidades.",
            "Con un tamaño máximo de 1,200 pies² y capacidad de dos pisos, puede construir una unidad espaciosa y atractiva que genere ingresos mensuales significativos.",
          ],
        },
        {
          heading: "Regulaciones de ADU en Chula Vista",
          paragraphs: [
            "Chula Vista permite ADUs independientes de hasta 1,200 pies² con retiros de 4 pies. Los diseños de dos pisos están permitidos dentro del límite de 16 pies de altura. No se requiere estacionamiento adicional bajo SB 13.",
            "La ciudad también permite ADUs Junior (JADUs) de hasta 500 pies² dentro de la huella de la vivienda existente, que se pueden construir además de una ADU completa.",
          ],
        },
        {
          heading: "EG Construction en Chula Vista",
          paragraphs: [
            "Nuestra oficina principal está ubicada en Chula Vista, lo que nos convierte en el constructor de ADU más accesible para los propietarios locales. Hemos completado docenas de ADUs en toda la ciudad y conocemos el departamento de planificación a fondo.",
            "Desde Otay Ranch hasta Rolling Hills, entendemos las condiciones únicas del sitio y las preferencias de diseño de cada vecindario de Chula Vista.",
          ],
        },
        {
          heading: "Proyectos Recientes de ADU en Chula Vista",
          paragraphs: [
            "Nuestro trabajo en Chula Vista incluye una ADU independiente de 749 pies² construida para renta, una conversión de garaje de 574 pies² que se rentó al terminarse y una conversión de garaje de 337 pies² construida como estudio familiar. Una ADU independiente de 529 pies² con el plano Toba está actualmente en diseño.",
            "Cada proyecto comenzó con una visita al terreno, una revisión de viabilidad según las reglas de ADU de Chula Vista y un precio fijo antes de los permisos. Puede ver fotos, planos y tiempos de cada uno en nuestra sección de proyectos.",
          ],
        },
        {
          heading: "¿ADU Independiente o Conversión de Garaje en Chula Vista?",
          paragraphs: [
            "Muchos lotes de Otay Ranch, Eastlake y Rancho Del Rey tienen espacio para una ADU independiente en el patio. Las casas más antiguas del oeste de Chula Vista (91910 y 91911) suelen tener un garaje que puede convertirse en una ADU completa con menos trabajo de cimentación nueva.",
            "Le ayudamos a comparar ambas opciones para su lote: tamaño, costo, potencial de renta y cómo encaja cada una en los planes de su familia. Si ya tiene planos aprobados, también podemos construir con ellos.",
          ],
        },
      ],
    },
  },

  // ─── SAN DIEGO ───────────────────────────────────────────────
  {
    slug: "san-diego",
    city: { en: "San Diego", es: "San Diego" },
    state: "CA",
    metaTitle: {
      en: "San Diego ADU Builder | 50+ Built, 60-Day Move-In",
      es: "Constructor ADU San Diego | 50+ Construidas",
    },
    metaDescription: {
      en: "San Diego ADU builder using City ADU Standard Plans to shortcut permits — 50+ built, 60-day move-in, price guarantee (Lic #864642).",
      es: "Constructor de ADU en San Diego. 50+ construidas, Planos Estándar, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta (#864642).",
    },
    intro: {
      en: "San Diego is the epicenter of ADU construction in Southern California. With the city's ADU Standard Plans program, strong rental markets in neighborhoods like North Park, Clairemont, and City Heights, and California's progressive ADU laws, there's never been a better time to build. EG Construction is your licensed local partner for ADU projects across the City of San Diego.",
      es: "San Diego es el epicentro de la construcción de ADU en el sur de California. Con el programa de planos estándar de ADU de la ciudad, mercados de alquiler fuertes en vecindarios como North Park, Clairemont y City Heights, y las leyes progresivas de ADU de California, nunca ha habido un mejor momento para construir.",
    },
    neighborhoods: ["North Park", "Clairemont", "City Heights", "University Heights", "South Park", "Linda Vista", "Mission Hills", "Normal Heights", "Kensington"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "2–6 months" },
      { label: { en: "Standard Plans", es: "Planos Estándar" }, value: "Available (city program)" },
    ],
    rentalIncome: [
      { zone: "North Park", oneBr: "$2,200", twoBr: "$2,800", threeBr: "$3,400" },
      { zone: "Clairemont", oneBr: "$2,100", twoBr: "$2,650", threeBr: "$3,250" },
      { zone: "City Heights", oneBr: "$2,000", twoBr: "$2,500", threeBr: "$3,100" },
      { zone: "University Heights", oneBr: "$2,150", twoBr: "$2,700", threeBr: "$3,300" },
    ],
    hasPreApprovedPlans: true,
    preApprovedNote: {
      en: "The City of San Diego offers ADU Standard Plans — pre-reviewed designs that expedite the permit process. EG Construction works with these plans and can also create custom designs tailored to your property.",
      es: "La Ciudad de San Diego ofrece planos estándar de ADU — diseños pre-revisados que aceleran el proceso de permisos. EG Construction trabaja con estos planos y también puede crear diseños personalizados para su propiedad.",
    },
    faq: [
      {
        q: { en: "How much does an ADU cost in San Diego?", es: "¿Cuánto cuesta una ADU en San Diego?" },
        a: {
          en: "ADU costs in San Diego range from $150,000 to $350,000+ depending on size, type, location, and finishes. A 600 sqft detached ADU typically starts around $180,000.",
          es: "Los costos de ADU en San Diego van desde $150,000 hasta $350,000+ dependiendo del tamaño, tipo, ubicación y acabados. Una ADU independiente de 600 pies² típicamente comienza alrededor de $180,000.",
        },
      },
      {
        q: { en: "What are San Diego's ADU Standard Plans?", es: "¿Qué son los planos estándar de ADU de San Diego?" },
        a: {
          en: "San Diego's ADU Standard Plans are pre-approved designs that the city has already reviewed. Using them can reduce your permit timeline by 2–3 months and lower your design costs.",
          es: "Los planos estándar de ADU de San Diego son diseños pre-aprobados que la ciudad ya ha revisado. Usarlos puede reducir su tiempo de permisos en 2–3 meses y reducir sus costos de diseño.",
        },
      },
      {
        q: { en: "Can I build an ADU in the historic districts of San Diego?", es: "¿Puedo construir una ADU en los distritos históricos de San Diego?" },
        a: {
          en: "Yes, ADUs are allowed in historic districts, but additional design review may be required to ensure compatibility with the neighborhood character. EG Construction has experience navigating these requirements.",
          es: "Sí, las ADUs están permitidas en distritos históricos, pero puede requerirse revisión de diseño adicional para asegurar compatibilidad con el carácter del vecindario. EG Construction tiene experiencia navegando estos requisitos.",
        },
      },
      {
        q: { en: "How long does permitting take in the City of San Diego?", es: "¿Cuánto tiempo toma el permiso en la Ciudad de San Diego?" },
        a: {
          en: "Permitting in San Diego typically takes 2–6 months depending on plan complexity and city workload. Using standard plans can significantly speed up the process.",
          es: "Los permisos en San Diego típicamente toman 2–6 meses dependiendo de la complejidad del plano y la carga de trabajo de la ciudad. Usar planos estándar puede acelerar significativamente el proceso.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in San Diego?",
          paragraphs: [
            "San Diego has one of the strongest rental markets in California. With median rents for a 1-bedroom apartment exceeding $2,200 in many neighborhoods, an ADU can pay for itself in just a few years while adding significant value to your property.",
            "The city's ADU Standard Plans program and California's progressive AB 68 and SB 13 laws make the process smoother than ever.",
          ],
        },
        {
          heading: "San Diego ADU Regulations",
          paragraphs: [
            "The City of San Diego allows ADUs up to 1,200 sqft with 4-foot setbacks. No additional parking is required under SB 13. The city also permits JADUs up to 500 sqft within the existing home.",
            "San Diego's Development Services Department handles ADU permits, and the typical timeline is 2–6 months depending on whether you use standard plans or a custom design.",
          ],
        },
        {
          heading: "EG Construction's San Diego ADU Expertise",
          paragraphs: [
            "We've built ADUs across dozens of San Diego neighborhoods, from the tight lots of North Park to the hillside properties of Mission Hills. Our team understands the city's planning requirements, site conditions, and design preferences.",
            "Every project includes design, engineering, permits, and full construction — all managed by a single point of contact for a stress-free experience.",
          ],
        },
        {
          heading: "Guest House Builder in San Diego",
          paragraphs: [
            "Looking for a guest house builder in San Diego? Under California law, a backyard guest house with its own kitchen and bathroom is permitted as a detached ADU. That means it can be up to 1,200 sqft in the City of San Diego, needs only 4-foot side and rear setbacks, and requires no extra parking.",
            "EG Construction designs, permits and builds guest houses for visiting family, aging parents, adult children or rental income — from compact studios to 2-bedroom layouts and 2-story designs.",
          ],
          links: [
            { label: "Detached ADU / guest house builders in San Diego", to: "/detached-adu-san-diego" },
            { label: "Browse guest house floor plans", to: "/floor-plans" },
            { label: "ADU price per sq ft in San Diego", to: "/blog/adu-cost-san-diego-2026" },
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en San Diego?",
          paragraphs: [
            "San Diego tiene uno de los mercados de alquiler más fuertes de California. Con alquileres medios para un apartamento de 1 habitación que superan los $2,200 en muchos vecindarios, una ADU puede pagarse por sí misma en solo unos años mientras agrega valor significativo a su propiedad.",
            "El programa de planos estándar de ADU de la ciudad y las leyes progresivas AB 68 y SB 13 de California hacen el proceso más fluido que nunca.",
          ],
        },
        {
          heading: "Regulaciones de ADU en San Diego",
          paragraphs: [
            "La Ciudad de San Diego permite ADUs de hasta 1,200 pies² con retiros de 4 pies. No se requiere estacionamiento adicional bajo SB 13. La ciudad también permite JADUs de hasta 500 pies² dentro de la vivienda existente.",
            "El Departamento de Servicios de Desarrollo de San Diego maneja los permisos de ADU, y el plazo típico es de 2–6 meses dependiendo de si usa planos estándar o un diseño personalizado.",
          ],
        },
        {
          heading: "Experiencia de EG Construction en ADUs de San Diego",
          paragraphs: [
            "Hemos construido ADUs en docenas de vecindarios de San Diego, desde los lotes estrechos de North Park hasta las propiedades en ladera de Mission Hills. Nuestro equipo entiende los requisitos de planificación, condiciones del sitio y preferencias de diseño de la ciudad.",
            "Cada proyecto incluye diseño, ingeniería, permisos y construcción completa — todo gestionado por un solo punto de contacto para una experiencia sin estrés.",
          ],
        },
        {
          heading: "Constructor de Casas de Huéspedes en San Diego",
          paragraphs: [
            "¿Busca un constructor de casas de huéspedes (guest house) en San Diego? Bajo la ley de California, una casa de huéspedes en el patio con cocina y baño propios se permite como ADU independiente. Puede medir hasta 1,200 pies² en la Ciudad de San Diego, solo necesita retiros laterales y traseros de 4 pies y no requiere estacionamiento adicional.",
            "EG Construction diseña, tramita y construye casas de huéspedes para familiares de visita, padres mayores, hijos adultos o ingresos por renta, desde estudios compactos hasta diseños de 2 recámaras y de 2 pisos.",
          ],
          links: [
            { label: "Constructores de ADU independientes / casas de huéspedes", to: "/detached-adu-san-diego" },
            { label: "Ver planos de casas de huéspedes", to: "/floor-plans" },
            { label: "Precio por pie² de una ADU en San Diego", to: "/blog/adu-cost-san-diego-2026" },
          ],
        },
      ],
    },
  },

  // ─── BONITA ──────────────────────────────────────────────────
  {
    slug: "bonita",
    city: { en: "Bonita", es: "Bonita" },
    state: "CA",
    metaTitle: {
      en: "Bonita ADU Builder | Licensed #864642, 4.8★ Reviews",
      es: "Constructor ADU Bonita | Licenciado #864642, 4.8★",
    },
    metaDescription: {
      en: "Bonita ADU builder handling San Diego County permits end-to-end — 50+ built, 60-day move-in, price guarantee. Book a call (Lic #864642).",
      es: "Constructor de ADU en Bonita (Lic #864642). 50+ construidas, permisos del condado, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta.",
    },
    intro: {
      en: "Bonita is an unincorporated community in San Diego County known for its spacious lots, quiet neighborhoods, and family-friendly atmosphere. ADU construction in Bonita falls under San Diego County jurisdiction, which allows units up to 1,200 sqft. With strong rental demand and generous lot sizes, Bonita is an excellent location for an ADU.",
      es: "Bonita es una comunidad no incorporada del condado de San Diego conocida por sus lotes espaciosos, vecindarios tranquilos y ambiente familiar. La construcción de ADU en Bonita cae bajo la jurisdicción del condado de San Diego, que permite unidades de hasta 1,200 pies². Con una fuerte demanda de alquiler y lotes generosos, Bonita es una excelente ubicación para una ADU.",
    },
    neighborhoods: ["Bonita Long Canyon", "Lynwood Hills", "Villas De Bonita", "Rancho De La Nacion", "Sunnyside"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–5 months" },
      { label: { en: "Jurisdiction", es: "Jurisdicción" }, value: "San Diego County" },
    ],
    rentalIncome: [
      { zone: "Bonita (91902)", oneBr: "$2,050", twoBr: "$2,600", threeBr: "$3,100" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "Bonita (San Diego County jurisdiction) does not currently offer pre-approved ADU plans. EG Construction provides proven designs that we've successfully permitted in the area.",
      es: "Bonita (jurisdicción del condado de San Diego) no ofrece actualmente planos de ADU pre-aprobados. EG Construction proporciona diseños probados que hemos permitido exitosamente en el área.",
    },
    faq: [
      {
        q: { en: "Who handles ADU permits in Bonita?", es: "¿Quién maneja los permisos de ADU en Bonita?" },
        a: {
          en: "Since Bonita is unincorporated, ADU permits are handled by San Diego County's Planning & Development Services. EG Construction manages the entire process.",
          es: "Como Bonita no está incorporada, los permisos de ADU son manejados por los Servicios de Planificación y Desarrollo del Condado de San Diego. EG Construction maneja todo el proceso.",
        },
      },
      {
        q: { en: "How much does an ADU cost in Bonita?", es: "¿Cuánto cuesta una ADU en Bonita?" },
        a: {
          en: "A typical ADU in Bonita costs $160,000–$320,000 depending on size and finishes. The larger lot sizes in Bonita often make construction more straightforward.",
          es: "Una ADU típica en Bonita cuesta $160,000–$320,000 dependiendo del tamaño y acabados. Los lotes más grandes en Bonita a menudo hacen la construcción más sencilla.",
        },
      },
      {
        q: { en: "Can I rent my ADU in Bonita?", es: "¿Puedo alquilar mi ADU en Bonita?" },
        a: {
          en: "Yes! Once you receive the Certificate of Occupancy, your ADU is legal to rent. ADUs in Bonita typically command $2,050–$3,100/mo depending on size.",
          es: "¡Sí! Una vez que reciba el Certificado de Ocupación, su ADU es legal para alquilar. Las ADUs en Bonita típicamente obtienen $2,050–$3,100/mes dependiendo del tamaño.",
        },
      },
      {
        q: { en: "Are there HOA restrictions on ADUs in Bonita?", es: "¿Hay restricciones de HOA para ADUs en Bonita?" },
        a: {
          en: "Under California law (AB 68), HOAs cannot prohibit ADUs. However, some design guidelines may apply. EG Construction helps navigate any HOA requirements.",
          es: "Bajo la ley de California (AB 68), las HOAs no pueden prohibir ADUs. Sin embargo, algunas pautas de diseño pueden aplicar. EG Construction ayuda a navegar cualquier requisito de HOA.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in Bonita?",
          paragraphs: [
            "Bonita's larger lot sizes make it one of the best communities for ADU construction in the South Bay. With less crowded properties and strong rental demand, you can build a spacious ADU that commands premium rents.",
            "The Cruz family, long-time Bonita residents, trusted EG Construction to build their ADU and couldn't be happier with the results.",
          ],
        },
        {
          heading: "Bonita ADU Regulations",
          paragraphs: [
            "As an unincorporated area, Bonita follows San Diego County ADU regulations. ADUs up to 1,200 sqft are allowed with 4-foot setbacks. The permit process typically takes 3–5 months through the county.",
          ],
        },
        {
          heading: "Building Your ADU in Bonita",
          paragraphs: [
            "EG Construction has extensive experience building ADUs in Bonita. We understand the county permitting process, local soil conditions, and the design aesthetic that fits this community. From Bonita Long Canyon to Lynwood Hills, we've helped homeowners add value and income to their properties.",
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en Bonita?",
          paragraphs: [
            "Los lotes más grandes de Bonita la convierten en una de las mejores comunidades para la construcción de ADU en South Bay. Con propiedades menos congestionadas y fuerte demanda de alquiler, puede construir una ADU espaciosa que obtiene alquileres premium.",
            "La familia Cruz, residentes de Bonita por mucho tiempo, confiaron en EG Construction para construir su ADU y no podrían estar más contentos con los resultados.",
          ],
        },
        {
          heading: "Regulaciones de ADU en Bonita",
          paragraphs: [
            "Como área no incorporada, Bonita sigue las regulaciones de ADU del condado de San Diego. Se permiten ADUs de hasta 1,200 pies² con retiros de 4 pies. El proceso de permisos típicamente toma 3–5 meses a través del condado.",
          ],
        },
        {
          heading: "Construyendo Su ADU en Bonita",
          paragraphs: [
            "EG Construction tiene amplia experiencia construyendo ADUs en Bonita. Entendemos el proceso de permisos del condado, las condiciones locales del suelo y la estética de diseño que se adapta a esta comunidad. Desde Bonita Long Canyon hasta Lynwood Hills, hemos ayudado a propietarios a agregar valor e ingresos a sus propiedades.",
          ],
        },
      ],
    },
  },

  // ─── LA MESA ─────────────────────────────────────────────────
  {
    slug: "la-mesa",
    city: { en: "La Mesa", es: "La Mesa" },
    state: "CA",
    metaTitle: {
      en: "La Mesa ADU Builder | Price Guarantee, 50+ Built",
      es: "Constructor ADU La Mesa | Garantía de Precio, 50+",
    },
    metaDescription: {
      en: "La Mesa ADU builder — 50+ built, permits in 3–5 months, 60-day move-in, price guarantee. Schedule your discovery call (Lic #864642).",
      es: "Constructor de ADU en La Mesa. 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★, permisos en 3–5 meses. Agenda tu consulta (#864642).",
    },
    intro: {
      en: "La Mesa — known as 'The Jewel of the Hills' — is one of the most desirable East County communities for ADU construction. With its walkable downtown village, excellent schools, and strong rental market, building an ADU in La Mesa is a smart financial move. EG Construction handles everything from design to final inspection.",
      es: "La Mesa — conocida como 'La Joya de las Colinas' — es una de las comunidades más deseables del este del condado para la construcción de ADU. Con su centro peatonal, excelentes escuelas y fuerte mercado de alquiler, construir una ADU en La Mesa es una decisión financiera inteligente.",
    },
    neighborhoods: ["Mount Helix", "Grossmont", "Rolando", "La Mesa Village", "Fletcher Hills", "Casa de Oro"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–5 months" },
      { label: { en: "Pre-Approved Plans", es: "Planos Pre-Aprobados" }, value: "No" },
    ],
    rentalIncome: [
      { zone: "La Mesa (91941)", oneBr: "$2,000", twoBr: "$2,550", threeBr: "$3,150" },
      { zone: "La Mesa (91942)", oneBr: "$1,925", twoBr: "$2,450", threeBr: "$3,000" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "La Mesa does not currently offer pre-approved ADU plans. EG Construction provides custom and proven designs that we've successfully permitted in the city multiple times.",
      es: "La Mesa no ofrece actualmente planos de ADU pre-aprobados. EG Construction proporciona diseños personalizados y probados que hemos permitido exitosamente en la ciudad múltiples veces.",
    },
    faq: [
      {
        q: { en: "How much does an ADU cost in La Mesa?", es: "¿Cuánto cuesta una ADU en La Mesa?" },
        a: {
          en: "ADU costs in La Mesa typically range from $155,000 to $310,000 depending on size, design, and site conditions. Hillside lots (like Mount Helix) may require additional engineering.",
          es: "Los costos de ADU en La Mesa típicamente van desde $155,000 hasta $310,000 dependiendo del tamaño, diseño y condiciones del sitio. Los lotes en ladera (como Mount Helix) pueden requerir ingeniería adicional.",
        },
      },
      {
        q: { en: "Can I build an ADU on a hillside lot in La Mesa?", es: "¿Puedo construir una ADU en un lote en ladera en La Mesa?" },
        a: {
          en: "Yes, we regularly build ADUs on hillside lots in areas like Mount Helix. Additional engineering and grading may be required, but it's absolutely doable.",
          es: "Sí, construimos ADUs regularmente en lotes en ladera en áreas como Mount Helix. Puede requerirse ingeniería y nivelación adicional, pero es absolutamente factible.",
        },
      },
      {
        q: { en: "What's the rental market like in La Mesa?", es: "¿Cómo es el mercado de alquiler en La Mesa?" },
        a: {
          en: "La Mesa has strong rental demand driven by its proximity to SDSU, Grossmont College, and downtown San Diego. A 2-bedroom ADU in 91941 can rent for $2,550/mo or more.",
          es: "La Mesa tiene una fuerte demanda de alquiler impulsada por su proximidad a SDSU, Grossmont College y el centro de San Diego. Una ADU de 2 habitaciones en 91941 puede alquilarse por $2,550/mes o más.",
        },
      },
      {
        q: { en: "How long does ADU permitting take in La Mesa?", es: "¿Cuánto tiempo toma el permiso de ADU en La Mesa?" },
        a: {
          en: "The La Mesa permitting process typically takes 3–5 months. EG Construction handles all submissions, plan check corrections, and inspections on your behalf.",
          es: "El proceso de permisos de La Mesa típicamente toma 3–5 meses. EG Construction maneja todas las presentaciones, correcciones de revisión de planos e inspecciones en su nombre.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in La Mesa?",
          paragraphs: [
            "La Mesa combines small-town charm with urban convenience. Its walkable downtown, excellent dining scene, and proximity to major employers make it highly desirable for renters. An ADU can generate $2,000–$3,150/mo while adding 20–30% to your property value.",
          ],
        },
        {
          heading: "La Mesa ADU Regulations",
          paragraphs: [
            "La Mesa allows ADUs up to 1,200 sqft with 4-foot setbacks. The maximum height is 16 feet. Like other California cities, no additional parking is required under SB 13. The city processes permits in 3–5 months on average.",
          ],
        },
        {
          heading: "Our La Mesa ADU Experience",
          paragraphs: [
            "EG Construction has built ADUs across La Mesa's diverse neighborhoods — from the flat lots of La Mesa Village to the hillside properties of Mount Helix. We handle the unique challenges of each area, including grading, retaining walls, and hillside engineering.",
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en La Mesa?",
          paragraphs: [
            "La Mesa combina el encanto de pueblo pequeño con la conveniencia urbana. Su centro peatonal, excelente escena gastronómica y proximidad a empleadores importantes la hacen muy deseable para inquilinos. Una ADU puede generar $2,000–$3,150/mes mientras agrega 20–30% al valor de su propiedad.",
          ],
        },
        {
          heading: "Regulaciones de ADU en La Mesa",
          paragraphs: [
            "La Mesa permite ADUs de hasta 1,200 pies² con retiros de 4 pies. La altura máxima es de 16 pies. Como otras ciudades de California, no se requiere estacionamiento adicional bajo SB 13. La ciudad procesa permisos en 3–5 meses en promedio.",
          ],
        },
        {
          heading: "Nuestra Experiencia con ADUs en La Mesa",
          paragraphs: [
            "EG Construction ha construido ADUs en los diversos vecindarios de La Mesa — desde los lotes planos de La Mesa Village hasta las propiedades en ladera de Mount Helix. Manejamos los desafíos únicos de cada área, incluyendo nivelación, muros de contención e ingeniería de ladera.",
          ],
        },
      ],
    },
  },

  // ─── CARLSBAD ────────────────────────────────────────────────
  {
    slug: "carlsbad",
    city: { en: "Carlsbad", es: "Carlsbad" },
    state: "CA",
    metaTitle: {
      en: "ADU Builder in Carlsbad, CA | Coastal Permits, Lic #864642",
      es: "Constructor de ADU en Carlsbad, CA | Zona Costera",
    },
    metaDescription: {
      en: "ADU builder in Carlsbad, CA: accessory dwelling units in La Costa, Bressi Ranch, Aviara and the Village. Coastal permits handled, 50+ built, price guarantee.",
      es: "Constructor de ADU en Carlsbad. Experiencia en zona costera, 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta (#864642).",
    },
    intro: {
      en: "Carlsbad is a premier North County coastal city with some of the highest rental demand in San Diego County. Building an ADU in Carlsbad offers exceptional return on investment, with rental rates reaching $2,200–$3,500/mo. EG Construction navigates Carlsbad's coastal zone requirements and local regulations to deliver beautiful, code-compliant ADUs.",
      es: "Carlsbad es una ciudad costera premier del norte del condado con una de las demandas de alquiler más altas del condado de San Diego. Construir una ADU en Carlsbad ofrece un retorno de inversión excepcional, con tasas de alquiler que alcanzan $2,200–$3,500/mes.",
    },
    neighborhoods: ["Bressi Ranch", "La Costa", "Aviara", "Carlsbad Village", "Calavera Hills", "Poinsettia"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–6 months" },
      { label: { en: "Coastal Zone", es: "Zona Costera" }, value: "Some areas (additional review)" },
    ],
    rentalIncome: [
      { zone: "Carlsbad (92008)", oneBr: "$2,200", twoBr: "$2,800", threeBr: "$3,300" },
      { zone: "Carlsbad (92009)", oneBr: "$2,300", twoBr: "$2,900", threeBr: "$3,500" },
      { zone: "Carlsbad (92011)", oneBr: "$2,250", twoBr: "$2,850", threeBr: "$3,400" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "Carlsbad does not currently offer pre-approved ADU plans. EG Construction provides custom designs optimized for Carlsbad's coastal aesthetic and building requirements.",
      es: "Carlsbad no ofrece actualmente planos de ADU pre-aprobados. EG Construction proporciona diseños personalizados optimizados para la estética costera y requisitos de construcción de Carlsbad.",
    },
    faq: [
      {
        q: { en: "Can I build an ADU in Carlsbad's coastal zone?", es: "¿Puedo construir una ADU en la zona costera de Carlsbad?" },
        a: {
          en: "Yes, ADUs are allowed in coastal zones, but additional Coastal Development Permit review may apply. EG Construction handles this process for you.",
          es: "Sí, las ADUs están permitidas en zonas costeras, pero puede aplicar una revisión adicional del Permiso de Desarrollo Costero. EG Construction maneja este proceso por usted.",
        },
      },
      {
        q: { en: "How much does an ADU cost in Carlsbad?", es: "¿Cuánto cuesta una ADU en Carlsbad?" },
        a: {
          en: "ADUs in Carlsbad typically cost $175,000–$350,000 depending on size, finishes, and whether the property is in a coastal zone. Premium finishes are popular in Carlsbad's upscale neighborhoods.",
          es: "Las ADUs en Carlsbad típicamente cuestan $175,000–$350,000 dependiendo del tamaño, acabados y si la propiedad está en zona costera.",
        },
      },
      {
        q: { en: "What's the rental market in Carlsbad?", es: "¿Cómo es el mercado de alquiler en Carlsbad?" },
        a: {
          en: "Carlsbad has one of the strongest rental markets in North County. A 2-bedroom ADU can rent for $2,800–$2,900/mo, making it an excellent investment.",
          es: "Carlsbad tiene uno de los mercados de alquiler más fuertes del norte del condado. Una ADU de 2 habitaciones puede alquilarse por $2,800–$2,900/mes.",
        },
      },
      {
        q: { en: "Does Carlsbad have Mello-Roos fees for ADUs?", es: "¿Carlsbad tiene tarifas Mello-Roos para ADUs?" },
        a: {
          en: "Some Carlsbad neighborhoods like Bressi Ranch and Robertson Ranch are in Mello-Roos districts. These fees may apply to ADU construction. We help you determine applicable fees before starting.",
          es: "Algunos vecindarios de Carlsbad como Bressi Ranch y Robertson Ranch están en distritos Mello-Roos. Estas tarifas pueden aplicar a la construcción de ADU. Le ayudamos a determinar las tarifas aplicables antes de comenzar.",
        },
      },
      {
        q: { en: "How long does it take to get an ADU permit in Carlsbad?", es: "¿Cuánto tarda un permiso de ADU en Carlsbad?" },
        a: {
          en: "Most Carlsbad ADU permits take 3–6 months. Lots in the Coastal Zone can take longer because of the added Coastal Development Permit review.",
          es: "La mayoría de los permisos de ADU en Carlsbad toman 3–6 meses. Los lotes en la Zona Costera pueden tardar más por la revisión adicional del Permiso de Desarrollo Costero.",
        },
      },
      {
        q: { en: "Can my HOA in La Costa or Aviara stop me from building an ADU?", es: "¿Mi HOA en La Costa o Aviara puede impedir que construya una ADU?" },
        a: {
          en: "No. California law prevents HOAs from prohibiting ADUs. They can apply reasonable design rules, which we incorporate into your plans.",
          es: "No. La ley de California impide que las HOA prohíban las ADU. Pueden aplicar reglas de diseño razonables, que incorporamos en sus planos.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in Carlsbad?",
          paragraphs: [
            "Carlsbad's coastal location, top-rated schools, and thriving tech industry (with employers like Viasat and ThermoFisher) create consistent demand for quality rental housing. ADUs in Carlsbad command some of the highest rents in North County.",
            "Whether you're building for rental income, a family member, or a home office, an ADU in Carlsbad adds significant value to your property.",
          ],
        },
        {
          heading: "Carlsbad ADU Regulations",
          paragraphs: [
            "Carlsbad allows ADUs up to 1,200 sqft with 4-foot setbacks. Properties in the coastal zone may require additional review. The permit timeline is typically 3–6 months.",
            "No additional parking is required under SB 13, and HOAs cannot prohibit ADU construction under California law.",
          ],
        },
        {
          heading: "Our Carlsbad ADU Process",
          paragraphs: [
            "EG Construction has experience building ADUs in Carlsbad's diverse neighborhoods — from the village area to master-planned communities like Bressi Ranch. We design ADUs that complement the coastal aesthetic while maximizing your investment.",
          ],
        },
        {
          heading: "Building an Accessory Dwelling Unit in Carlsbad by Neighborhood",
          paragraphs: [
            "Carlsbad Village and areas west of I-5 are more likely to fall in the Coastal Zone, where a Coastal Development Permit review can add time. Master-planned communities like Bressi Ranch, La Costa and Aviara often have HOAs — they can set design standards but cannot ban an accessory dwelling unit under California law. Calavera Hills and Poinsettia lots frequently have slopes that call for a soils report.",
            "We check your lot's zoning, coastal status, HOA rules and Mello-Roos before design, so your Carlsbad ADU builder quote is based on real site conditions.",
          ],
        },
        {
          heading: "Carlsbad ADU Permit Steps",
          paragraphs: [
            "1) Site visit and feasibility. 2) Design and engineering. 3) Submittal to the City of Carlsbad (plus Coastal review when required). 4) Corrections and permit issuance, typically 3–6 months total. 5) Construction and final inspection.",
          ],
          links: [
            { label: "Detached ADU builders in San Diego County", to: "/detached-adu-san-diego" },
            { label: "Garage conversion ADUs", to: "/garage-conversion-adu-san-diego" },
            { label: "ADU builders in Oceanside", to: "/areas/oceanside" },
            { label: "ADU builders in Encinitas", to: "/areas/encinitas" },
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en Carlsbad?",
          paragraphs: [
            "La ubicación costera de Carlsbad, escuelas de primera categoría y próspera industria tecnológica crean demanda constante de vivienda de alquiler de calidad. Las ADUs en Carlsbad obtienen algunos de los alquileres más altos del norte del condado.",
            "Ya sea que construya para ingresos por alquiler, un familiar o una oficina en casa, una ADU en Carlsbad agrega valor significativo a su propiedad.",
          ],
        },
        {
          heading: "Regulaciones de ADU en Carlsbad",
          paragraphs: [
            "Carlsbad permite ADUs de hasta 1,200 pies² con retiros de 4 pies. Las propiedades en la zona costera pueden requerir revisión adicional. El tiempo de permisos es típicamente de 3–6 meses.",
            "No se requiere estacionamiento adicional bajo SB 13, y las HOAs no pueden prohibir la construcción de ADU bajo la ley de California.",
          ],
        },
        {
          heading: "Nuestro Proceso de ADU en Carlsbad",
          paragraphs: [
            "EG Construction tiene experiencia construyendo ADUs en los diversos vecindarios de Carlsbad — desde el área del pueblo hasta comunidades planificadas como Bressi Ranch. Diseñamos ADUs que complementan la estética costera mientras maximizan su inversión.",
          ],
        },
        {
          heading: "Construir una Unidad de Vivienda Accesoria en Carlsbad por Vecindario",
          paragraphs: [
            "Carlsbad Village y las zonas al oeste de la I-5 tienen más probabilidad de estar en la Zona Costera, donde la revisión del Permiso de Desarrollo Costero puede agregar tiempo. Comunidades planificadas como Bressi Ranch, La Costa y Aviara suelen tener HOA: pueden fijar estándares de diseño, pero no prohibir una ADU bajo la ley de California. Los lotes de Calavera Hills y Poinsettia con frecuencia tienen pendiente y requieren estudio de suelos.",
            "Revisamos zonificación, estatus costero, reglas de HOA y Mello-Roos antes de diseñar, para que su cotización se base en las condiciones reales del lote.",
          ],
        },
        {
          heading: "Pasos del Permiso de ADU en Carlsbad",
          paragraphs: [
            "1) Visita y factibilidad. 2) Diseño e ingeniería. 3) Entrega a la Ciudad de Carlsbad (más revisión costera cuando aplique). 4) Correcciones y emisión del permiso, normalmente 3–6 meses en total. 5) Construcción e inspección final.",
          ],
          links: [
            { label: "Constructores de ADU independientes en el Condado de San Diego", to: "/detached-adu-san-diego" },
            { label: "Conversiones de garaje a ADU", to: "/garage-conversion-adu-san-diego" },
            { label: "Constructores de ADU en Oceanside", to: "/areas/oceanside" },
            { label: "Constructores de ADU en Encinitas", to: "/areas/encinitas" },
          ],
        },
      ],
    },
  },

  // ─── SAN DIEGO COUNTY ────────────────────────────────────────
  {
    slug: "san-diego-county",
    city: { en: "San Diego County", es: "Condado de San Diego" },
    state: "CA",
    metaTitle: {
      en: "San Diego County ADU Builder | 50+ Built, 4.8★",
      es: "Constructor ADU Condado de San Diego | 50+ y 4.8★",
    },
    metaDescription: {
      en: "San Diego County ADU builder for unincorporated areas — Alpine, Fallbrook, Lakeside, Ramona. 50+ built, 60-day move-in, price guarantee.",
      es: "Constructor de ADU en el condado de San Diego (áreas no incorporadas). 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta.",
    },
    intro: {
      en: "San Diego County's unincorporated areas — from Alpine to Fallbrook, Lakeside to Ramona — offer unique opportunities for ADU construction. With larger lots and growing rental demand, homeowners in the county can build spacious ADUs up to 1,200 sqft. EG Construction specializes in county-permitted ADU projects.",
      es: "Las áreas no incorporadas del condado de San Diego — desde Alpine hasta Fallbrook, Lakeside hasta Ramona — ofrecen oportunidades únicas para la construcción de ADU. Con lotes más grandes y demanda de alquiler creciente, los propietarios en el condado pueden construir ADUs espaciosas de hasta 1,200 pies².",
    },
    neighborhoods: ["Alpine", "Lakeside", "Ramona", "Fallbrook", "Valley Center", "Jamul", "Spring Valley", "Lemon Grove"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–6 months" },
      { label: { en: "Septic/Sewer", es: "Séptico/Alcantarillado" }, value: "Varies by area" },
    ],
    rentalIncome: [
      { zone: "Alpine/Lakeside", oneBr: "$1,800", twoBr: "$2,300", threeBr: "$2,800" },
      { zone: "Spring Valley", oneBr: "$1,750", twoBr: "$2,200", threeBr: "$2,700" },
      { zone: "Fallbrook/Valley Center", oneBr: "$1,900", twoBr: "$2,400", threeBr: "$2,900" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "San Diego County does not offer pre-approved plans for unincorporated areas. EG Construction provides designs proven to meet county requirements.",
      es: "El condado de San Diego no ofrece planos pre-aprobados para áreas no incorporadas. EG Construction proporciona diseños probados para cumplir con los requisitos del condado.",
    },
    faq: [
      {
        q: { en: "Can I build an ADU on a septic system?", es: "¿Puedo construir una ADU con sistema séptico?" },
        a: {
          en: "Yes, but you may need to upgrade or expand your septic system. EG Construction coordinates septic evaluations and upgrades as part of the ADU project.",
          es: "Sí, pero puede necesitar actualizar o expandir su sistema séptico. EG Construction coordina evaluaciones y actualizaciones sépticas como parte del proyecto de ADU.",
        },
      },
      {
        q: { en: "Are ADUs allowed on rural properties in the county?", es: "¿Se permiten ADUs en propiedades rurales del condado?" },
        a: {
          en: "Yes, California law allows ADUs on virtually all residential properties, including rural lots. Larger lots often make ADU construction easier and more cost-effective.",
          es: "Sí, la ley de California permite ADUs en prácticamente todas las propiedades residenciales, incluyendo lotes rurales. Los lotes más grandes a menudo hacen la construcción de ADU más fácil y rentable.",
        },
      },
      {
        q: { en: "How are county ADU permits different from city permits?", es: "¿Cómo son diferentes los permisos de ADU del condado de los de la ciudad?" },
        a: {
          en: "County permits are processed by San Diego County Planning & Development Services instead of a city department. The requirements are similar, but site-specific factors like septic, well water, and fire access may apply.",
          es: "Los permisos del condado son procesados por los Servicios de Planificación y Desarrollo del Condado en lugar de un departamento de la ciudad. Los requisitos son similares, pero factores específicos del sitio como séptico, agua de pozo y acceso para bomberos pueden aplicar.",
        },
      },
      {
        q: { en: "Do I need fire access for my ADU in the county?", es: "¿Necesito acceso para bomberos para mi ADU en el condado?" },
        a: {
          en: "In fire-prone areas, additional fire access and brush clearance requirements may apply. EG Construction evaluates fire safety requirements during the initial consultation.",
          es: "En áreas propensas a incendios, pueden aplicar requisitos adicionales de acceso para bomberos y limpieza de maleza. EG Construction evalúa los requisitos de seguridad contra incendios durante la consulta inicial.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "ADU Construction in Unincorporated San Diego County",
          paragraphs: [
            "Unincorporated San Diego County offers some of the best opportunities for ADU construction. Larger lot sizes, fewer density constraints, and growing rental demand make it an attractive option for homeowners from Alpine to Fallbrook.",
          ],
        },
        {
          heading: "County ADU Regulations",
          paragraphs: [
            "San Diego County allows ADUs up to 1,200 sqft with 4-foot setbacks. Properties on septic systems may need evaluations. Fire-prone areas have additional access requirements. The permit timeline is typically 3–6 months.",
          ],
        },
        {
          heading: "Why Choose EG Construction for County ADUs",
          paragraphs: [
            "We understand the unique challenges of building in unincorporated areas — from septic systems to fire access to rural site conditions. Our team handles every aspect of the project, ensuring a smooth process from start to finish.",
          ],
        },
      ],
      es: [
        {
          heading: "Construcción de ADU en el Condado de San Diego No Incorporado",
          paragraphs: [
            "El condado de San Diego no incorporado ofrece algunas de las mejores oportunidades para la construcción de ADU. Lotes más grandes, menos restricciones de densidad y demanda de alquiler creciente lo convierten en una opción atractiva para propietarios desde Alpine hasta Fallbrook.",
          ],
        },
        {
          heading: "Regulaciones de ADU del Condado",
          paragraphs: [
            "El condado de San Diego permite ADUs de hasta 1,200 pies² con retiros de 4 pies. Las propiedades con sistemas sépticos pueden necesitar evaluaciones. Las áreas propensas a incendios tienen requisitos adicionales de acceso. El plazo de permisos es típicamente de 3–6 meses.",
          ],
        },
        {
          heading: "Por Qué Elegir EG Construction para ADUs del Condado",
          paragraphs: [
            "Entendemos los desafíos únicos de construir en áreas no incorporadas — desde sistemas sépticos hasta acceso para bomberos y condiciones de sitio rurales. Nuestro equipo maneja cada aspecto del proyecto, asegurando un proceso fluido de principio a fin.",
          ],
        },
      ],
    },
  },

  // ─── ENCINITAS ───────────────────────────────────────────────
  {
    slug: "encinitas",
    city: { en: "Encinitas", es: "Encinitas" },
    state: "CA",
    metaTitle: {
      en: "Encinitas Custom ADU Builder | Coastal, 60-Day Move-In",
      es: "Constructor ADU Encinitas | Costera, 60 días",
    },
    metaDescription: {
      en: "Encinitas ADU builder with coastal-zone permitting expertise — 50+ built, 60-day move-in, price guarantee. Book a call (Lic #864642).",
      es: "Constructor de ADU en Encinitas. Experiencia en zona costera, 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta (#864642).",
    },
    intro: {
      en: "Encinitas is a highly sought-after coastal city with premium property values and some of the highest rental rates in San Diego County. ADU construction in Encinitas offers exceptional ROI, but requires careful navigation of coastal zone regulations. EG Construction has the expertise to build beautiful, compliant ADUs in this desirable community.",
      es: "Encinitas es una ciudad costera muy cotizada con valores de propiedad premium y algunas de las tasas de alquiler más altas del condado de San Diego. La construcción de ADU en Encinitas ofrece un ROI excepcional, pero requiere navegación cuidadosa de las regulaciones de zona costera.",
    },
    neighborhoods: ["Leucadia", "Cardiff-by-the-Sea", "Olivenhain", "New Encinitas", "Old Encinitas", "Village Park"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–7 months" },
      { label: { en: "Coastal Zone", es: "Zona Costera" }, value: "Much of city (additional review)" },
    ],
    rentalIncome: [
      { zone: "Encinitas (92024)", oneBr: "$2,300", twoBr: "$2,900", threeBr: "$3,500" },
      { zone: "Cardiff (92007)", oneBr: "$2,400", twoBr: "$3,000", threeBr: "$3,600" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "Encinitas does not offer pre-approved ADU plans. Due to the coastal zone, custom designs are typically recommended. EG Construction creates plans tailored to Encinitas's unique requirements.",
      es: "Encinitas no ofrece planos de ADU pre-aprobados. Debido a la zona costera, se recomiendan típicamente diseños personalizados. EG Construction crea planos adaptados a los requisitos únicos de Encinitas.",
    },
    faq: [
      {
        q: { en: "How does the coastal zone affect my ADU in Encinitas?", es: "¿Cómo afecta la zona costera a mi ADU en Encinitas?" },
        a: {
          en: "Properties in the coastal zone may require a Coastal Development Permit, which adds review time. EG Construction navigates this process efficiently to minimize delays.",
          es: "Las propiedades en la zona costera pueden requerir un Permiso de Desarrollo Costero, que agrega tiempo de revisión. EG Construction navega este proceso eficientemente para minimizar retrasos.",
        },
      },
      {
        q: { en: "How much does an ADU cost in Encinitas?", es: "¿Cuánto cuesta una ADU en Encinitas?" },
        a: {
          en: "ADUs in Encinitas typically cost $180,000–$380,000 depending on size, location, and finishes. Coastal zone properties may have slightly higher costs due to additional requirements.",
          es: "Las ADUs en Encinitas típicamente cuestan $180,000–$380,000 dependiendo del tamaño, ubicación y acabados. Las propiedades en zona costera pueden tener costos ligeramente más altos.",
        },
      },
      {
        q: { en: "What's the rental income potential in Encinitas?", es: "¿Cuál es el potencial de ingresos por alquiler en Encinitas?" },
        a: {
          en: "Encinitas commands some of the highest ADU rents in the county. A 2-bedroom ADU in Cardiff can rent for $3,000/mo or more, making it one of the best ROI locations.",
          es: "Encinitas obtiene algunos de los alquileres de ADU más altos del condado. Una ADU de 2 habitaciones en Cardiff puede alquilarse por $3,000/mes o más.",
        },
      },
      {
        q: { en: "Can I build an ADU in Olivenhain?", es: "¿Puedo construir una ADU en Olivenhain?" },
        a: {
          en: "Yes! Olivenhain's larger lot sizes make it excellent for ADU construction. The area is outside the coastal zone, which can simplify permitting.",
          es: "¡Sí! Los lotes más grandes de Olivenhain lo hacen excelente para la construcción de ADU. El área está fuera de la zona costera, lo que puede simplificar los permisos.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in Encinitas?",
          paragraphs: [
            "Encinitas offers the highest rental rates in North County, driven by its beach lifestyle, excellent schools, and proximity to biotech employers along the I-5 corridor. ADU rental income of $2,300–$3,600/mo makes it one of the best investments in San Diego County.",
          ],
        },
        {
          heading: "Encinitas ADU Regulations",
          paragraphs: [
            "Encinitas allows ADUs up to 1,200 sqft with 4-foot setbacks. Much of the city falls within the coastal zone, requiring additional environmental review. The permit timeline ranges from 3–7 months depending on coastal zone status.",
          ],
        },
        {
          heading: "Building ADUs in Encinitas with EG Construction",
          paragraphs: [
            "Our team has experience with Encinitas's unique coastal requirements and design sensibilities. We create ADUs that complement the laid-back beach aesthetic while maximizing functionality and rental value.",
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en Encinitas?",
          paragraphs: [
            "Encinitas ofrece las tasas de alquiler más altas del norte del condado, impulsadas por su estilo de vida playero, excelentes escuelas y proximidad a empleadores biotecnológicos a lo largo del corredor I-5. Los ingresos por alquiler de ADU de $2,300–$3,600/mes la convierten en una de las mejores inversiones del condado.",
          ],
        },
        {
          heading: "Regulaciones de ADU en Encinitas",
          paragraphs: [
            "Encinitas permite ADUs de hasta 1,200 pies² con retiros de 4 pies. Gran parte de la ciudad cae dentro de la zona costera, requiriendo revisión ambiental adicional. El plazo de permisos varía de 3–7 meses dependiendo del estado de zona costera.",
          ],
        },
        {
          heading: "Construyendo ADUs en Encinitas con EG Construction",
          paragraphs: [
            "Nuestro equipo tiene experiencia con los requisitos costeros únicos de Encinitas y sus sensibilidades de diseño. Creamos ADUs que complementan la estética relajada de playa mientras maximizan funcionalidad y valor de alquiler.",
          ],
        },
      ],
    },
  },

  // ─── ESCONDIDO ───────────────────────────────────────────────
  {
    slug: "escondido",
    city: { en: "Escondido", es: "Escondido" },
    state: "CA",
    metaTitle: {
      en: "Escondido ADU Builder | Price Guarantee, 4.8★",
      es: "Constructor ADU Escondido | Garantía de Precio, 4.8★",
    },
    metaDescription: {
      en: "Escondido ADU builder — 50+ built, permits in 2 to 5 months, 60-day move-in, price guarantee. Schedule your discovery call (Lic #864642).",
      es: "Constructor de ADU en Escondido. 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★, permisos en 2–5 meses. Agenda tu consulta (#864642).",
    },
    intro: {
      en: "Escondido offers some of the most affordable entry points for ADU construction in North County San Diego. With spacious lots, growing rental demand, and a revitalizing downtown, an ADU in Escondido is a smart investment. EG Construction builds ADUs throughout the city — from the flat lots near Grand Avenue to the hillside properties in the eastern foothills.",
      es: "Escondido ofrece algunos de los puntos de entrada más accesibles para la construcción de ADU en el norte del condado de San Diego. Con lotes espaciosos, demanda de alquiler creciente y un centro en revitalización, una ADU en Escondido es una inversión inteligente.",
    },
    neighborhoods: ["Downtown Escondido", "Westside", "East Valley", "Hidden Meadows", "Felicita", "Bear Valley"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "2 to 5 months" },
      { label: { en: "Pre-Approved Plans", es: "Planos Pre-Aprobados" }, value: "No" },
    ],
    rentalIncome: [
      { zone: "Escondido (92025)", oneBr: "$1,700", twoBr: "$2,200", threeBr: "$2,600" },
      { zone: "Escondido (92026)", oneBr: "$1,750", twoBr: "$2,250", threeBr: "$2,700" },
      { zone: "Escondido (92027)", oneBr: "$1,700", twoBr: "$2,150", threeBr: "$2,550" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "Escondido does not currently offer pre-approved ADU plans. EG Construction provides proven designs that have been permitted multiple times in the city.",
      es: "Escondido no ofrece actualmente planos de ADU pre-aprobados. EG Construction proporciona diseños probados que han sido permitidos múltiples veces en la ciudad.",
    },
    faq: [
      {
        q: { en: "How much does an ADU cost in Escondido?", es: "¿Cuánto cuesta una ADU en Escondido?" },
        a: {
          en: "ADUs in Escondido typically cost $145,000–$290,000 depending on size and finishes. Lower land costs and easier site access often make Escondido ADUs more affordable than coastal cities.",
          es: "Las ADUs en Escondido típicamente cuestan $145,000–$290,000 dependiendo del tamaño y acabados. Los costos de tierra más bajos y acceso más fácil al sitio a menudo hacen las ADUs de Escondido más accesibles que las ciudades costeras.",
        },
      },
      {
        q: { en: "Is Escondido a good city for ADU rental income?", es: "¿Es Escondido una buena ciudad para ingresos por alquiler de ADU?" },
        a: {
          en: "Yes! While rents are slightly lower than coastal cities, the lower construction costs mean better ROI. A 2-bedroom ADU in Escondido can rent for $2,200/mo, offering a strong cash-on-cash return.",
          es: "¡Sí! Aunque los alquileres son ligeramente más bajos que en las ciudades costeras, los costos de construcción más bajos significan mejor ROI. Una ADU de 2 habitaciones en Escondido puede alquilarse por $2,200/mes.",
        },
      },
      {
        q: { en: "How long does ADU permitting take in Escondido?", es: "¿Cuánto tarda el permiso de ADU en Escondido?" },
        a: {
          en: "Escondido's permitting process typically takes 2 to 5 months. The city has been processing ADU permits efficiently as demand has grown.",
          es: "El proceso de permisos de Escondido típicamente toma 2–5 meses. La ciudad ha estado procesando permisos de ADU eficientemente a medida que la demanda ha crecido.",
        },
      },
      {
        q: { en: "Can I build an ADU on a large lot in the Escondido hills?", es: "¿Puedo construir una ADU en un lote grande en las colinas de Escondido?" },
        a: {
          en: "Absolutely. Larger lots in east Escondido are ideal for detached ADUs. Additional engineering may be needed for hillside lots, but the extra space makes for excellent designs.",
          es: "Absolutamente. Los lotes más grandes en el este de Escondido son ideales para ADUs independientes. Puede necesitarse ingeniería adicional para lotes en ladera, pero el espacio extra permite excelentes diseños.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in Escondido?",
          paragraphs: [
            "Escondido's combination of affordable lot sizes, growing population, and revitalizing downtown makes it one of the best ROI locations for ADU construction in San Diego County. Lower construction costs compared to coastal cities mean your investment pays off faster.",
          ],
        },
        {
          heading: "Escondido ADU Regulations",
          paragraphs: [
            "Escondido allows ADUs up to 1,200 sqft with 4-foot setbacks and a 16-foot height limit. The permit process is typically 2 to 5 months. No additional parking is required under SB 13.",
          ],
        },
        {
          heading: "EG Construction in Escondido",
          paragraphs: [
            "We've built ADUs throughout Escondido, from downtown properties to larger eastern lots. Our team understands the city's planning requirements and delivers quality ADUs on time and on budget.",
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en Escondido?",
          paragraphs: [
            "La combinación de lotes accesibles, población creciente y centro en revitalización de Escondido la convierten en una de las mejores ubicaciones de ROI para la construcción de ADU en el condado de San Diego.",
          ],
        },
        {
          heading: "Regulaciones de ADU en Escondido",
          paragraphs: [
            "Escondido permite ADUs de hasta 1,200 pies² con retiros de 4 pies y un límite de 16 pies de altura. El proceso de permisos es típicamente de 2–5 meses. No se requiere estacionamiento adicional bajo SB 13.",
          ],
        },
        {
          heading: "EG Construction en Escondido",
          paragraphs: [
            "Hemos construido ADUs en todo Escondido, desde propiedades del centro hasta lotes más grandes del este. Nuestro equipo entiende los requisitos de planificación de la ciudad y entrega ADUs de calidad a tiempo y dentro del presupuesto.",
          ],
        },
      ],
    },
  },

  // ─── POWAY ───────────────────────────────────────────────────
  {
    slug: "poway",
    city: { en: "Poway", es: "Poway" },
    state: "CA",
    metaTitle: {
      en: "Poway ADU Builder | Licensed, 4.8★ Reviews",
      es: "Constructor ADU Poway | Licenciado, 4.8★",
    },
    metaDescription: {
      en: "Poway ADU builder for large lots and PUSD schools — 50+ built, permits in 3–5 months, price guarantee. Book a call (Lic #864642).",
      es: "Constructor de ADU en Poway (Lic #864642). 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★, permisos en 3–5 meses. Agenda tu consulta.",
    },
    intro: {
      en: "Poway — 'The City in the Country' — offers ideal conditions for ADU construction. With large lot sizes, excellent schools (Poway Unified), and strong family-oriented rental demand, an ADU in Poway can generate premium rents while adding substantial property value. EG Construction brings expert ADU building to this desirable community.",
      es: "Poway — 'La Ciudad en el Campo' — ofrece condiciones ideales para la construcción de ADU. Con lotes grandes, excelentes escuelas (Poway Unified) y fuerte demanda de alquiler familiar, una ADU en Poway puede generar alquileres premium mientras agrega valor sustancial a la propiedad.",
    },
    neighborhoods: ["Poway Road Corridor", "Garden Road", "Twin Peaks", "Old Poway", "South Poway", "Stoneridge"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "3–5 months" },
      { label: { en: "Pre-Approved Plans", es: "Planos Pre-Aprobados" }, value: "No" },
    ],
    rentalIncome: [
      { zone: "Poway (92064)", oneBr: "$2,100", twoBr: "$2,700", threeBr: "$3,200" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "Poway does not currently offer pre-approved ADU plans. EG Construction provides custom and proven designs suited to Poway's larger lot characteristics.",
      es: "Poway no ofrece actualmente planos de ADU pre-aprobados. EG Construction proporciona diseños personalizados y probados adaptados a las características de lotes más grandes de Poway.",
    },
    faq: [
      {
        q: { en: "How much does an ADU cost in Poway?", es: "¿Cuánto cuesta una ADU en Poway?" },
        a: {
          en: "ADUs in Poway typically cost $160,000–$330,000 depending on size and finishes. Poway's larger lots often make site work easier, potentially reducing costs.",
          es: "Las ADUs en Poway típicamente cuestan $160,000–$330,000 dependiendo del tamaño y acabados. Los lotes más grandes de Poway a menudo facilitan el trabajo del sitio.",
        },
      },
      {
        q: { en: "Why is Poway good for ADU rental income?", es: "¿Por qué Poway es bueno para ingresos por alquiler de ADU?" },
        a: {
          en: "Poway's top-rated school district drives strong demand from families seeking quality housing. A 2-bedroom ADU can rent for $2,700/mo, with consistent occupancy.",
          es: "El distrito escolar de primera categoría de Poway impulsa fuerte demanda de familias que buscan vivienda de calidad. Una ADU de 2 habitaciones puede alquilarse por $2,700/mes.",
        },
      },
      {
        q: { en: "Are there fire safety requirements for ADUs in Poway?", es: "¿Hay requisitos de seguridad contra incendios para ADUs en Poway?" },
        a: {
          en: "Some Poway properties are in fire-prone zones requiring additional brush clearance and fire-resistant materials. EG Construction evaluates these requirements during the initial assessment.",
          es: "Algunas propiedades de Poway están en zonas propensas a incendios que requieren limpieza adicional de maleza y materiales resistentes al fuego. EG Construction evalúa estos requisitos durante la evaluación inicial.",
        },
      },
      {
        q: { en: "Can I build a detached ADU on my Poway property?", es: "¿Puedo construir una ADU independiente en mi propiedad de Poway?" },
        a: {
          en: "Absolutely! Poway's generous lot sizes are ideal for detached ADUs. Most properties can easily accommodate a 1,200 sqft detached unit with proper setbacks.",
          es: "¡Absolutamente! Los lotes generosos de Poway son ideales para ADUs independientes. La mayoría de las propiedades pueden acomodar fácilmente una unidad independiente de 1,200 pies² con los retiros adecuados.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "Why Build an ADU in Poway?",
          paragraphs: [
            "Poway's 'City in the Country' character means larger lots, easier construction access, and strong demand from families seeking its top-rated schools. ADU rental rates of $2,100–$3,200/mo combined with reasonable construction costs make Poway one of the best ROI locations in the county.",
          ],
        },
        {
          heading: "Poway ADU Regulations",
          paragraphs: [
            "Poway allows ADUs up to 1,200 sqft with 4-foot setbacks and 16-foot height limits. No additional parking is required. The permit process takes 3–5 months on average.",
          ],
        },
        {
          heading: "EG Construction in Poway",
          paragraphs: [
            "Our team has built ADUs throughout Poway, leveraging the generous lot sizes to create spacious, attractive units. We handle design, permits, and construction — delivering your ADU on time and on budget.",
          ],
        },
      ],
      es: [
        {
          heading: "¿Por Qué Construir una ADU en Poway?",
          paragraphs: [
            "El carácter de 'Ciudad en el Campo' de Poway significa lotes más grandes, acceso de construcción más fácil y fuerte demanda de familias que buscan sus escuelas de primera categoría. Las tasas de alquiler de ADU de $2,100–$3,200/mes combinadas con costos de construcción razonables hacen de Poway una de las mejores ubicaciones de ROI en el condado.",
          ],
        },
        {
          heading: "Regulaciones de ADU en Poway",
          paragraphs: [
            "Poway permite ADUs de hasta 1,200 pies² con retiros de 4 pies y límites de 16 pies de altura. No se requiere estacionamiento adicional. El proceso de permisos toma 3–5 meses en promedio.",
          ],
        },
        {
          heading: "EG Construction en Poway",
          paragraphs: [
            "Nuestro equipo ha construido ADUs en todo Poway, aprovechando los lotes generosos para crear unidades espaciosas y atractivas. Manejamos diseño, permisos y construcción — entregando su ADU a tiempo y dentro del presupuesto.",
          ],
        },
      ],
    },
  },

  // ─── VISTA ───────────────────────────────────────────────────
  {
    slug: "vista",
    city: { en: "Vista", es: "Vista" },
    state: "CA",
    metaTitle: {
      en: "ADU Builder in Vista, CA | Permits + 60-Day Move-In",
      es: "Constructor de ADU en Vista, CA | Permisos y 60 Días",
    },
    metaDescription: {
      en: "Licensed ADU builder in Vista, CA (Lic #864642). Detached ADUs, garage conversions and JADUs from $145K, permits in 2 to 5 months, 60-day move-in.",
      es: "Constructor de ADU con licencia en Vista, CA (#864642). ADUs independientes, conversiones de garaje y JADUs desde $145K, permisos en 2–5 meses.",
    },
    geo: { lat: 33.2, lng: -117.2425 },
    postalCodes: ["92081", "92083", "92084"],
    heroAlt: {
      en: "Residential street in Vista, California at sunset, where EG Construction builds ADUs",
      es: "Calle residencial en Vista, California al atardecer, donde EG Construction construye ADUs",
    },
    heroImage: "/__l5e/assets-v1/09a9d149-47dd-4d05-8b8d-4834e146b9b6/vista-hero.png",

    intro: {
      en: "We build detached ADUs, garage conversions and JADUs in Vista, permitted through the City of Vista and quoted with a written price guarantee. Vista lots are big enough that a detached unit works on most properties. The hillside parcels in Shadowridge and Breeze Hill are the exception, since those need grading and retaining work, which we do in house. And because a lot of Vista housing went up in the 1970s and 80s, a service panel upgrade is often part of the job. We price that into the original quote instead of bringing it back later as a change order.",
      es: "Construimos ADUs en Vista, CA — unidades independientes, conversiones de garaje y JADUs, permitidas ante la Ciudad de Vista y entregadas con garantía de precio por escrito. Los lotes amplios de Vista hacen viable una ADU independiente en la mayoría de propiedades, mientras que los terrenos en ladera de Shadowridge y Breeze Hill requieren movimiento de tierra y muros de contención que hacemos con nuestro propio equipo. Muchas casas de Vista son de los años 70 y 80, así que la actualización del panel eléctrico suele ser parte del alcance — lo cotizamos desde el inicio, no como orden de cambio.",
    },
    neighborhoods: ["Shadowridge", "Vista Village", "Breeze Hill", "Rancho Buena Vista", "Lake View", "Mar Vista"],
    facts: [
      { label: { en: "Max ADU Size", es: "Tamaño Máximo de ADU" }, value: "1,200 sqft" },
      { label: { en: "Side/Rear Setbacks", es: "Retiros Laterales/Traseros" }, value: "4 ft" },
      { label: { en: "Max Height", es: "Altura Máxima" }, value: "16 ft" },
      { label: { en: "Parking Required", es: "Estacionamiento Requerido" }, value: "No (SB 13)" },
      { label: { en: "Permit Timeline", es: "Tiempo de Permiso" }, value: "2 to 5 months" },
      { label: { en: "Pre-Approved Plans", es: "Planos Pre-Aprobados" }, value: "No" },
    ],
    rentalIncome: [
      { zone: "Vista (92081)", oneBr: "$1,800", twoBr: "$2,300", threeBr: "$2,700" },
      { zone: "Vista (92083)", oneBr: "$1,850", twoBr: "$2,350", threeBr: "$2,800" },
      { zone: "Vista (92084)", oneBr: "$1,800", twoBr: "$2,250", threeBr: "$2,650" },
    ],
    hasPreApprovedPlans: false,
    preApprovedNote: {
      en: "Vista does not currently offer pre-approved ADU plans. EG Construction provides proven designs that we've successfully permitted in the city.",
      es: "Vista no ofrece actualmente planos de ADU pre-aprobados. EG Construction proporciona diseños probados que hemos permitido exitosamente en la ciudad.",
    },
    faq: [
      {
        q: { en: "How much does an ADU cost in Vista?", es: "¿Cuánto cuesta una ADU en Vista?" },
        a: {
          en: "ADUs in Vista typically run $145,000–$285,000 depending on size, finishes and site conditions. A 546 sqft one-bedroom sits at the low end; a 1,200 sqft two- or three-bedroom at the high end. Hillside lots, long utility runs and electrical panel upgrades are the three items that move a Vista budget most.",
          es: "Las ADUs en Vista cuestan típicamente $145,000–$285,000 según tamaño, acabados y condiciones del sitio. Una de 546 pies² con una recámara está en el rango bajo; una de 1,200 pies² con dos o tres recámaras en el alto. Los lotes en ladera, los trazos largos de servicios y la actualización del panel eléctrico son los tres factores que más mueven el presupuesto en Vista.",
        },
      },
      {
        q: { en: "What's the rental market like in Vista?", es: "¿Cómo es el mercado de alquiler en Vista?" },
        a: {
          en: "Vista's rental market has grown alongside North County development. A 2-bedroom ADU rents around $2,300/mo across the 92081, 92083 and 92084 zips, with strong year-round occupancy driven by workers at the business park and nearby Palomar College students.",
          es: "El mercado de alquiler de Vista ha crecido junto con el desarrollo del norte del condado. Una ADU de 2 recámaras se alquila cerca de $2,300/mes en los códigos 92081, 92083 y 92084, con buena ocupación todo el año.",
        },
      },
      {
        q: { en: "How long does ADU permitting take in Vista?", es: "¿Cuánto tarda el permiso de ADU en Vista?" },
        a: {
          en: "Plan on 2 to 5 months from submittal to issued permit with the City of Vista. State law caps ADU plan-check review at 60 days per cycle; most Vista projects clear in one or two cycles when the site plan, Title 24 and structural calcs are complete at submittal.",
          es: "Calcula de 2 a 5 meses desde la entrega hasta el permiso emitido con la Ciudad de Vista. La ley estatal limita la revisión de planos de ADU a 60 días por ciclo; la mayoría de proyectos en Vista se resuelven en uno o dos ciclos si el plano de sitio, Title 24 y los cálculos estructurales van completos.",
        },
      },
      {
        q: { en: "Can I build an ADU in Shadowridge (Vista)?", es: "¿Puedo construir una ADU en Shadowridge (Vista)?" },
        a: {
          en: "Yes. Shadowridge has an HOA, but California law prohibits HOAs from banning ADUs. Design guidelines on roof pitch, stucco color and window trim may still apply, and we prepare the HOA architectural submittal alongside the city plan set.",
          es: "Sí. Shadowridge tiene HOA, pero la ley de California prohíbe a las HOAs prohibir ADUs. Pueden aplicar pautas de diseño sobre pendiente del techo, color del estuco y molduras, y preparamos la solicitud arquitectónica de la HOA junto con el juego de planos para la ciudad.",
        },
      },
      {
        q: { en: "Can I build an ADU on a sloped lot in Vista?", es: "¿Puedo construir una ADU en un lote inclinado en Vista?" },
        a: {
          en: "Yes, and it's common in Vista. Sloped lots usually need a retaining wall, engineered grading and sometimes a stepped foundation. We just completed a 749 sqft hillside ADU in nearby Bonita with a retaining wall, so the detail work is familiar. Expect $15,000–$40,000 of site work on top of the base build.",
          es: "Sí, y es común en Vista. Los lotes inclinados normalmente requieren muro de contención, movimiento de tierra con ingeniería y a veces cimentación escalonada. Acabamos de terminar una ADU de 749 pies² en ladera en Bonita con muro de contención. Prevé de $15,000 a $40,000 de obra de sitio sobre el costo base.",
        },
      },
      {
        q: { en: "What's the difference between an ADU and a JADU in Vista?", es: "¿Cuál es la diferencia entre una ADU y una JADU en Vista?" },
        a: {
          en: "A JADU is capped at 500 sqft, must be carved out of the existing house footprint, and can share a bathroom with the main home — but the owner must live on the property. A standard ADU can be detached, up to 1,200 sqft in Vista, and has no owner-occupancy requirement. Vista allows one ADU plus one JADU on most single-family lots.",
          es: "Una JADU está limitada a 500 pies², debe salir de la huella de la casa existente y puede compartir baño con la casa principal — pero el propietario debe vivir en la propiedad. Una ADU estándar puede ser independiente, hasta 1,200 pies² en Vista, y no exige ocupación del propietario. Vista permite una ADU más una JADU en la mayoría de lotes unifamiliares.",
        },
      },
      {
        q: { en: "Is a garage conversion cheaper than a detached ADU in Vista?", es: "¿Es más barata una conversión de garaje que una ADU independiente en Vista?" },
        a: {
          en: "Usually yes, because the slab, walls and roof already exist. A Vista garage conversion typically lands $95,000–$160,000 versus $145,000+ for detached. The trade-offs are size (most Vista garages are 380–440 sqft) and the loss of covered parking, which SB 13 no longer requires you to replace.",
          es: "Normalmente sí, porque la losa, los muros y el techo ya existen. Una conversión de garaje en Vista suele quedar entre $95,000 y $160,000 frente a $145,000+ de una independiente. Las contras son el tamaño (la mayoría de garajes en Vista son de 380–440 pies²) y perder el estacionamiento techado, que SB 13 ya no obliga a reponer.",
        },
      },
      {
        q: { en: "Can I sell the ADU separately from my Vista home?", es: "¿Puedo vender la ADU por separado de mi casa en Vista?" },
        a: {
          en: "Not by default. AB 1033 lets cities opt in to allowing ADUs to be sold as condos, and the City of Vista has not adopted it. For now a Vista ADU stays on the same parcel and title as the main home — its value shows up in appraisal and rental income rather than a separate sale.",
          es: "No por defecto. La ley AB 1033 permite a las ciudades autorizar la venta de ADUs como condominios, y la Ciudad de Vista no la ha adoptado. Por ahora una ADU en Vista permanece en la misma parcela y escritura que la casa principal — su valor se refleja en el avalúo y el ingreso por alquiler.",
        },
      },
    ],
    content: {
      en: [
        {
          heading: "ADU Cost in Vista, CA",
          paragraphs: [
            "These are the all in ranges we quote in Vista, covering design, engineering, city permits and construction. Two things push a Vista project toward the top of its range more often than anything else: site work on a sloped lot, and a service panel upgrade.",
          ],
          table: {
            headers: ["Size", "Layout", "Typical all-in cost", "Est. rent"],
            rows: [
              ["546 sqft", "1 bed / 1 bath", "$145,000 – $185,000", "$1,800/mo"],
              ["740–749 sqft", "2 bed / 1 bath", "$185,000 – $235,000", "$2,300/mo"],
              ["1,000–1,200 sqft", "2–3 bed / 2 bath", "$235,000 – $285,000", "$2,700/mo"],
            ],
          },
          links: [
            { label: "2026 San Diego ADU cost breakdown", to: "/blog/adu-cost-san-diego-2026" },
            { label: "ADU financing options in San Diego", to: "/adu-financing-san-diego" },
            { label: "Run your Vista ADU rental income numbers", to: "/roi-calculator" },
          ],
        },
        {
          heading: "Building an ADU by Vista Neighborhood",
          paragraphs: [
            "Shadowridge. Planned community with an HOA. State law protects your right to build an ADU, but expect architectural review on exterior finishes, and expect a few sloped lots that need retaining work.",
            "Vista Village. Older, smaller in town lots near downtown. Garage conversions and JADUs usually pencil better here than a full detached build, and rental demand from the brewery district is steady.",
            "Breeze Hill. Hillside parcels with grade changes. Detached ADUs work well, but budget for engineered grading, a retaining wall and a longer utility trench.",
            "Rancho Buena Vista. Large 1970s and 80s lots with real backyard depth. This is where a 740 to 1,200 sqft detached ADU fits most comfortably. Panel upgrades are common on homes of this era.",
            "Lake View and Mar Vista. Mixed lot sizes with good street access, which keeps crane and delivery costs down. Detached and attached both work, depending on rear yard depth.",
          ],
        },
        {
          heading: "The City of Vista ADU Permit Process",
          paragraphs: [
            "1. Site evaluation and feasibility. We confirm setbacks, lot coverage, slope, sewer or septic, and the existing electrical service before anything gets drawn.",
            "2. Design and construction documents. Floor plan, elevations, Title 24 energy compliance, structural calculations, and the HOA package where one applies.",
            "3. Plan check submittal to the City of Vista Community Development Department, with Vista Irrigation District water and Buena Sanitation sewer sign offs where required.",
            "4. Corrections and permit issuance. Usually one or two review cycles, 2 to 5 months in total.",
            "5. Construction and inspections. Foundation, framing, rough utilities, insulation, drywall and final, ending in the certificate of occupancy.",
          ],
          links: [
            { label: "See our completed Vista ADU project", to: "/projects/sunny-detached-adu-vista" },
            { label: "Browse pre-designed ADU floor plans", to: "/floor-plans" },
          ],
        },
        {
          heading: "EG Construction in Vista and North County",
          paragraphs: [
            "Project Sunny is a 740 sqft detached ADU we built in Vista for a family that wanted a second generation living on the property. It took 7 months, with high ceilings, an efficient HVAC system and a private patio. We work across North County, so the same crew and the same suppliers move between jobs here rather than getting subbed out every time.",
            "You get a written quote within 2 days and a price guarantee, from a licensed contractor: California #864642. Schedule a discovery call and we will tell you what fits your lot.",
          ],
          links: [
            { label: "ADU builder in Escondido", to: "/areas/escondido" },
            { label: "ADU builder in San Marcos", to: "/areas/san-marcos" },
            { label: "ADU builder in Carlsbad", to: "/areas/carlsbad" },
          ],
        },
      ],
      es: [
        {
          heading: "Costo de una ADU en Vista, CA",
          paragraphs: [
            "Estos son los rangos completos que cotizamos en Vista — diseño, ingeniería, permisos municipales y construcción. La obra de sitio en lotes inclinados y la actualización del panel eléctrico son los dos extras que más empujan un proyecto de Vista al tope de su rango.",
          ],
          table: {
            headers: ["Tamaño", "Distribución", "Costo típico total", "Renta est."],
            rows: [
              ["546 pies²", "1 recámara / 1 baño", "$145,000 – $185,000", "$1,800/mes"],
              ["740–749 pies²", "2 recámaras / 1 baño", "$185,000 – $235,000", "$2,300/mes"],
              ["1,000–1,200 pies²", "2–3 recámaras / 2 baños", "$235,000 – $285,000", "$2,700/mes"],
            ],
          },
          links: [
            { label: "Desglose de costos de ADU en San Diego 2026", to: "/blog/adu-cost-san-diego-2026" },
            { label: "Opciones de financiamiento para ADU en San Diego", to: "/adu-financing-san-diego" },
            { label: "Calcula el ingreso por renta de tu ADU en Vista", to: "/roi-calculator" },
          ],
        },
        {
          heading: "Construir una ADU por Vecindario en Vista",
          paragraphs: [
            "Shadowridge. Comunidad planificada con HOA. Las ADUs están protegidas por ley estatal, pero habrá revisión arquitectónica de acabados exteriores y algunos lotes con pendiente que requieren muro de contención.",
            "Vista Village. Lotes urbanos más antiguos y pequeños cerca del centro. Aquí las conversiones de garaje y las JADUs suelen convenir más que una ADU independiente completa, con demanda de renta constante por el distrito cervecero.",
            "Breeze Hill. Parcelas en ladera con cambios de nivel. Las ADUs independientes funcionan bien, pero hay que presupuestar movimiento de tierra con ingeniería, muro de contención y una zanja de servicios más larga.",
            "Rancho Buena Vista. Lotes amplios de los años 70 y 80 con buena profundidad de patio. Es donde mejor caben las ADUs independientes de 740 a 1,200 pies²; la actualización del panel es común en casas de esa época.",
            "Lake View y Mar Vista. Tamaños de lote mixtos con buen acceso desde la calle, lo que reduce costos de grúa y entregas. Funcionan tanto ADUs independientes como adosadas según la profundidad del patio.",
          ],
        },
        {
          heading: "Proceso de Permiso de ADU en la Ciudad de Vista",
          paragraphs: [
            "1. Evaluación del sitio y factibilidad — confirmamos retiros, cobertura del lote, pendiente, drenaje sanitario o séptico y el servicio eléctrico existente antes de dibujar nada.",
            "2. Diseño y documentos de construcción — plano de planta, elevaciones, cumplimiento energético Title 24 y cálculos estructurales, más el paquete para la HOA cuando aplica.",
            "3. Entrega a revisión de planos ante el Departamento de Desarrollo Comunitario de Vista, con las aprobaciones de Vista Irrigation District y Buena Sanitation cuando se requieren.",
            "4. Correcciones y emisión del permiso — normalmente uno o dos ciclos de revisión, de 2 a 5 meses en total.",
            "5. Construcción e inspecciones — cimentación, estructura, servicios en rough, aislamiento, tablaroca y final, hasta el certificado de ocupación.",
          ],
          links: [
            { label: "Ver nuestro proyecto de ADU terminado en Vista", to: "/projects/sunny-detached-adu-vista" },
            { label: "Explorar planos de ADU pre-diseñados", to: "/floor-plans" },
          ],
        },
        {
          heading: "EG Construction en Vista y el Norte del Condado",
          paragraphs: [
            "Construimos el Proyecto Sunny, una ADU independiente de 740 pies² en Vista, para una familia que planeaba vivienda multigeneracional — terminada en 7 meses con techos altos, HVAC eficiente y patio privado. Trabajamos en todo el norte del condado, así que el mismo equipo, proveedores e inspectores se repiten entre obras.",
            "Cada proyecto en Vista incluye cotización por escrito en 2 días, garantía de precio y licencia de California #864642. Agenda tu llamada de descubrimiento para ver qué cabe en tu lote.",
          ],
          links: [
            { label: "Constructor de ADU en Escondido", to: "/areas/escondido" },
            { label: "Constructor de ADU en San Marcos", to: "/areas/san-marcos" },
            { label: "Constructor de ADU en Carlsbad", to: "/areas/carlsbad" },
          ],
        },
      ],
    },
  },

];
